// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * IMPORTANT: SKU fields in audit schemas must remain nullable/nullish.
 * Legacy items created before the item migration refactor may have null SKUs.
 * The backend now requires SKU, but audit logs from before this change
 * will contain null values that must still parse correctly.
 */
import * as Sentry from '@sentry/react';
import { oauthScopeSchema } from '@/account/api/get-connected-applications';
import {
  attentionThresholdEntitySchema,
  ledgerAccountTypeSchema,
  serialPatternKindSchema,
} from '@/account/api/types';
import { responseMetadataSchema } from '@/api/types';
import { eventPropertyCarrierTrackingValueSchema } from '@/asset/api/types';
import {
  ASSET_FUNCTIONAL_STATUS_LABELS,
  assetFunctionalStatusSchema,
} from '@/constants/asset-functional-status';
import { attentionReasonSchema } from '@/constants/attention';
import { auditedMessageTypeSchema } from '@/constants/audited-message-type';
import { billingFrequencySchema } from '@/constants/billing-frequency';
import {
  DEPRECIATION_MODEL_LABELS,
  depreciationModelSchema,
} from '@/constants/depreciation-model';
import { notificationChannelSchema } from '@/constants/notification-channel';
import {
  PRODUCT_FIELD_TYPE_DATA,
  productFieldTypeSchema,
} from '@/constants/product-field-type';
import { shippingCarrierSchema } from '@/constants/shipping-carrier';
import { currencySchema as currencyPairSchema } from '@/currency/api/types';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import {
  FO_CLOSE_SOURCE_AUDIT_LABELS,
  FO_STATUS_AUDIT_LABELS,
} from '@/fulfillment/constants/fulfillment-order-status';
import { locationTypeSchema } from '@/location/api/types';
import { paymentTermSchema } from '@/payment-term/api/types';
import { transactionTypeSchema } from '@/project/api/types';
import { PO_STATUS_AUDIT_LABELS } from '@/purchase-order/constants/status';
import { RO_STATUS_AUDIT_LABELS } from '@/return-order/constants/status';
import {
  quarantineResolutionKindSchema,
  quarantineTypeSchema,
} from '@/shipment/api/types';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { DateTime } from 'luxon';
import { z } from 'zod';

/**
 * Creates a delta schema for tracking changes to data, including added, removed,
 * and updated items, based on the provided schema.
 *
 * @param {T} schema - The Zod schema representing the structure of individual items.
 * @return {z.ZodType} A Zod schema object with arrays for added, removed, and updated items.
 */
function createDeltaSchema<T extends z.ZodType>(schema: T) {
  return z
    .object({
      added: z.array(schema).optional(),
      removed: z.array(schema).optional(),
      updated: z
        .array(
          z.object({
            old: schema,
            new: schema,
          })
        )
        .optional(),
    })
    .transform((value) => {
      if (value.added === undefined) {
        value.added = [];
      }
      if (value.removed === undefined) {
        value.removed = [];
      }
      if (value.updated === undefined) {
        value.updated = [];
      }
      return value;
    });
}

/**
 * Creates a property schema with the provided Zod type schema and property name.
 *
 *  T = Zod type schema that is used for old/new value
 *  K = Property identifier such as "TEXT" or "LOCATION"
 *
 * @param {T} schema - The Zod schema defining the structure of the property.
 * @param {K} name - The name of the property to be used in the schema.
 * @return {z.ZodObject} A Zod schema object that represents the property schema.
 */
function createPropertySchema<T extends z.ZodType, K extends string>(
  schema: T,
  name: K
) {
  return z.object({
    id: z.string(),
    name: z.string(),
    type: z.literal(name),
    newValue: schema.nullable(),
    oldValue: schema.nullable().optional(),
    redacted: z.boolean().optional(),
  });
}

export type AuditEventTargetObjectTypeName =
  | 'Asset'
  | 'Bundle'
  | 'Charge'
  | 'Connected application'
  | 'Contact'
  | 'Cost'
  | 'Customer'
  | 'External notification'
  | 'Fulfillment order'
  | 'HubSpot configuration'
  | 'HubSpot sync'
  | 'Integration configuration'
  | 'Invoice'
  | 'Item'
  | 'Location'
  | 'Note'
  | 'Notification settings'
  | 'Organization'
  | 'Project'
  | 'Project deletion'
  | 'Purchase order'
  | 'Return order'
  | 'Salesforce configuration'
  | 'Shipment'
  | 'Ticket'
  | 'Time record'
  | 'User';

const auditEventContextActionSchema = z.enum([
  'CREATE',
  'UPDATE',
  'DELETE',
  'LOGIN',
  'LOGOUT',
  'MERGE',
]);

export type AuditEventContextAction = z.infer<
  typeof auditEventContextActionSchema
>;

const sparseEntitySchema = z
  .object({
    id: z.string(),
    key: z.string(),
    name: z.string().optional(),
    serial: z.string().optional(),
  })
  .transform(({ name, serial, ...opts }) => ({
    ...opts,
    name: name ?? serial ?? 'No name',
  }));

export type AuditSparseEntity = z.infer<typeof sparseEntitySchema>;

const auditEventContextActorSchema = z.object({
  id: z.string(),
  name: z.string(),
  primaryEmail: z.string(),
});

const auditSourceTypeSchema = z.enum([
  'UPLOAD',
  'API_KEY',
  'CARRIER_UPDATE',
  'HUBSPOT_API',
  'SFDC_API',
  'SFDC_SYNC',
  'THIRD_PARTY_API',
  'INHERIT',
  'NETSUITE',
  'QBO',
  'OAUTH',
]);

export type AuditSourceType = z.infer<typeof auditSourceTypeSchema>;

const groupReasonsSchema = z.enum([
  'CASCADE_PROJECT_DELETE',
  'RETROACTIVE_RECEIVE',
]);

const baseAuditEventContextSchema = z.object({
  userContext: auditEventContextActorSchema,
  contextType: auditEventContextActionSchema,
  groupKey: z.string().nullable().optional(),
  groupReason: groupReasonsSchema.nullable().optional().catch(null),
  source: z
    .object({
      id: z.string().nullable().optional(),
      name: z.string(),
      type: auditSourceTypeSchema,
    })
    .nullable()
    .optional(),
});

const siteMetadataSchema = z.object({
  address: z
    .object({ formattedAddress: z.string().nullable() })
    .nullable()
    .optional(),
  latitude: z.coerce.number().nullable().optional(),
  longitude: z.coerce.number().nullable().optional(),
  googlePlaceId: z.string().nullable().optional(),
});

// Both audit metadata formats normalize to this — all the UI needs is one
// formatted address line (rendered as a tooltip in audit-location-label.tsx).
const locationAuditMetadataSchema = z.object({
  address: z.object({ formattedAddress: z.string().nullable() }).nullable(),
});
type LocationAuditMetadata = z.infer<typeof locationAuditMetadataSchema>;

// Current format: `address` is a structured components object.
const currentLocationAuditMetadataSchema = z
  .object({
    address: z.object({ formattedAddress: z.string().nullish() }).nullish(),
  })
  .transform(
    (metadata): LocationAuditMetadata => ({
      address: { formattedAddress: metadata.address?.formattedAddress ?? null },
    })
  );

// Legacy format: `address` is a flat, pre-formatted string. Audit metadata is
// an immutable write-time snapshot, so older events retain this older shape.
const legacyLocationAuditMetadataSchema = z
  .object({ address: z.string().nullish() })
  .transform(
    (metadata): LocationAuditMetadata => ({
      address: { formattedAddress: metadata.address ?? null },
    })
  );

const locationSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  metadata: currentLocationAuditMetadataSchema
    .or(legacyLocationAuditMetadataSchema)
    .pipe(locationAuditMetadataSchema)
    .nullish(),
});

const locationPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_LOCATION'),
  newValue: locationSchema.nullable(),
  oldValue: locationSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

export type AuditLocationProperty = z.infer<typeof locationPropertySchema>;

const personSchema = z
  .object({ id: z.string(), key: z.string(), name: z.string() })
  .transform(({ name }) => name);

const customerSchema = z
  .object({ id: z.string(), key: z.string(), name: z.string() })
  .transform(({ name }) => name);

// The key is kept so the audit label can link to the fulfillment order.
const fulfillmentOrderSchema = z.object({
  id: z.string(),
  key: z.string(),
  foNumber: z.string(),
});

// The key is kept so the audit label can link to the return order.
const returnOrderSchema = z.object({
  id: z.string(),
  key: z.string(),
  roNumber: z.string(),
});

// The key is kept so the audit label can link to the purchase order.
const purchaseOrderSchema = z.object({
  id: z.string(),
  key: z.string(),
  poNumber: z.string(),
});

const cachedShipmentSchema = z.object({
  id: z.string(),
  key: z.string(),
  shipmentNumber: z.string(),
});

const companySchema = z
  .object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    type: z.enum(['MANUFACTURER', 'CUSTOMER']),
  })
  .transform(({ name }) => name);

const itemSchema = z
  .object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    sku: z.string().nullable().optional(),
  })
  .transform(({ name }) => name);

const projectSchema = z
  .object({ id: z.string(), key: z.string(), name: z.string() })
  .transform(({ name }) => name);

const assetFunctionalStatusValueSchema = assetFunctionalStatusSchema.transform(
  (value) => ASSET_FUNCTIONAL_STATUS_LABELS[value]
);

const depreciationModelValueSchema = depreciationModelSchema.transform(
  (value) => DEPRECIATION_MODEL_LABELS[value]
);

// Legacy CURRENCY audit values are bare decimal strings written before
// multi-currency; they were always USD.
const legacyAuditCurrencyValueSchema = z.string().transform((value, ctx) => {
  const amount = parseCurrencyAmount(value);

  if (amount === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Legacy currency value must be a numeric string',
    });
    return z.NEVER;
  }

  return formatCurrencyValue({ amount, isoCode: 'USD' }, { sparse: true });
});

const currencyObjectAuditValueSchema = currencyPairSchema.transform((value) =>
  formatCurrencyValue(value)
);

// Fields migrated to the currency pair write {amount, isoCode} objects while
// unmigrated CURRENCY fields and pre-migration rows still carry bare strings;
// both shapes normalize to a formatted display string.
const auditCurrencyValueSchema = currencyObjectAuditValueSchema.or(
  legacyAuditCurrencyValueSchema
);

const assetAssignmentSchema = z
  .object({
    id: z.string(),
    key: z.string(),
    serialNumber: z.string(),
    startOn: dateTimeSchema,
    endOn: dateTimeSchema.optional(),
  })
  .transform(({ serialNumber, startOn, endOn, ...data }) => ({
    ...data,
    serial: serialNumber,
    start: startOn,
    end: endOn,
  }));

const projectItemSchema = z
  .object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    quantity: z.number(),
  })
  .transform(({ quantity, ...data }) => ({ ...data, count: quantity }));

const fileSchema = z
  .object({
    id: z.string(),
    key: z.string(),
    // Old audits have "name", new ones have "filename"
    filename: z.string().optional(),
    name: z.string().optional(),
  })
  .transform(({ name, filename, ...opts }) => ({
    ...opts,
    filename: filename ?? name ?? 'Missing filename', // The last part is impossible in the audit, but needed for types
  }));

const cachedFileSchema = z.object({
  id: z.string(),
  name: z.string(),
  newValue: fileSchema.nullable().optional(),
  oldValue: fileSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

export type CachedFile = z.infer<typeof cachedFileSchema>;

const projectItemListSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_PROJECT_ITEM_LIST_DELTA'),
  newValue: z
    .array(projectItemSchema)
    .nullable()
    .transform((value) => value ?? []),
  oldValue: z.array(projectItemSchema).nullable().optional(),
  redacted: z.boolean().optional(),
});

export type ProjectItemList = z.infer<typeof projectItemListSchema>;

const projectLineItemEntitySchema = z
  .object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    sku: z.string().nullish(),
    type: z.enum(['PRODUCT', 'PART']).optional().default('PRODUCT'),
  })
  .nullable();

const projectLineItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  item: projectLineItemEntitySchema,
  transactionType: transactionTypeSchema.nullish(),
  location: z
    .object({ id: z.string(), key: z.string(), name: z.string() })
    .nullish(),
  details: z.string().nullable(),
  quantity: z.number().nullable(),
  billingFrequency: billingFrequencySchema.nullable(),
  unitPrice: auditCurrencyValueSchema.nullish().catch(null),
  startOn: z.string().nullish(),
  endOn: z.string().nullish(),
  aggregationKey: z.string().optional(),
  phaseRowNumber: z.number().optional(),
  pricingMode: z.string().nullish().catch(null),
  chargeSchedule: z.string().nullish().catch(null),
  chargeFrequency: z.string().nullish().catch(null),
  invoiceFrequency: z.string().nullish().catch(null),
  invoiceMode: z.string().nullish().catch(null),
  fixedPrice: auditCurrencyValueSchema.nullish().catch(null),
  totalOverride: auditCurrencyValueSchema.nullish().catch(null),
});

const projectLineItemsListSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_PROJECT_ITEM_CONFIGURATION_LIST'),
  newValue: z.array(projectLineItemSchema).catch([]),
  oldValue: z.array(projectLineItemSchema).catch([]),
  redacted: z.boolean().optional(),
});

export type ProjectLineItemsList = z.infer<typeof projectLineItemsListSchema>;

const locationListSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_LOCATION_LIST_DELTA'),
  newValue: z
    .array(z.object({ id: z.string(), key: z.string(), name: z.string() }))
    .nullable()
    .optional(),
  oldValue: z
    .array(z.object({ id: z.string(), key: z.string(), name: z.string() }))
    .nullable()
    .optional(),
  redacted: z.boolean().optional(),
});

export type LocationList = z.infer<typeof locationListSchema>;

const contactSchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  company: z.string().nullable().optional(),
});

const auditContactSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CONTACT'),
  newValue: contactSchema.nullable(),
  oldValue: contactSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

export type AuditContact = z.infer<typeof auditContactSchema>;

const assetAssignmentListSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_ASSET_ASSIGNMENT_LIST'),
  newValue: z
    .array(assetAssignmentSchema)
    .nullable()
    .optional()
    .transform((value) => value ?? []),
  oldValue: z
    .array(assetAssignmentSchema)
    .nullable()
    .optional()
    .transform((value) => value ?? []),
  redacted: z.boolean().optional(),
});

const assetMetadataValueSchema = z.object({
  id: z.string(),
  key: z.string(),
  type: z.object({
    id: z.string(),
    key: z.string(),
    type: productFieldTypeSchema,
    label: z.string(),
  }),
  // Force this to a string since we only support primitive metadata currently
  value: z.coerce.string().nullable(),
});

const assetMetadataSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_ASSET_METADATA'),
  newValue: assetMetadataValueSchema.nullable().catch(null),
  oldValue: assetMetadataValueSchema.nullable().catch(null),
  redacted: z.boolean().optional(),
});

export type AssetMetadata = z.infer<typeof assetMetadataSchema>;

const assetMetadataListSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_ASSET_METADATA_LIST'),
  newValue: z.array(assetMetadataValueSchema).catch([]),
  oldValue: z.array(assetMetadataValueSchema).catch([]),
  redacted: z.boolean().optional(),
});

export type AssetMetadataList = z.infer<typeof assetMetadataListSchema>;

const auditContextItemFieldTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('ITEM_FIELD_TYPE'),
  newValue: productFieldTypeSchema
    .transform((value) => PRODUCT_FIELD_TYPE_DATA[value].fieldTypeLabel)
    .nullable(),
  oldValue: productFieldTypeSchema
    .transform((value) => PRODUCT_FIELD_TYPE_DATA[value].fieldTypeLabel)
    .nullable()
    .optional(),
  redacted: z.boolean().optional(),
});

export type AssetAssignmentList = z.infer<typeof assetAssignmentListSchema>;

// An audit entry records the topic as it was named when the change happened,
// and a retired name is never rewritten, so the schema reads the retired names
// too rather than widening to a bare string.
const notificationConfigurationSchema = z.object({
  topic: auditedMessageTypeSchema,
  channels: z.array(notificationChannelSchema),
});

type NotificationConfiguration = z.infer<
  typeof notificationConfigurationSchema
>;

// A topic this bundle has never heard of is dropped on its own and reported,
// rather than failing the array and blanking the delta, which would render a
// preference change as no change at all.
function toAuditedConfigurations(
  value: NamedConfiguration[]
): NotificationConfiguration[] {
  const configurations: NotificationConfiguration[] = [];
  for (const entry of value) {
    const result = notificationConfigurationSchema.safeParse(entry);
    if (result.success) {
      configurations.push(result.data);
      continue;
    }
    if (import.meta.env.MODE === 'development') {
      console.error('Unknown notification topic:', entry, result.error);
    }
    Sentry.captureException(
      Error('Unknown notification topic', { cause: entry }),
      { extra: { entry, error: result.error } }
    );
  }
  return configurations;
}

// This layer only names the entry that could not be parsed, so it reads the
// topic and the channels as the free text they arrive as, and the strict schema
// above decides whether either is one the screen can draw.
const namedConfigurationSchema = z.object({
  topic: z.string(),
  channels: z.array(z.string()),
});

type NamedConfiguration = z.infer<typeof namedConfigurationSchema>;

const notificationConfigurationListSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_NOTIFICATION_DELTA_LIST'),
  newValue: z
    .array(namedConfigurationSchema)
    .catch([])
    .transform(toAuditedConfigurations),
  oldValue: z
    .array(namedConfigurationSchema)
    .catch([])
    .transform(toAuditedConfigurations),
  redacted: z.boolean().optional(),
});

export type NotificationConfigurationList = z.infer<
  typeof notificationConfigurationListSchema
>;

const hubspotEntityReferenceSchema = z.object({
  id: z.string(),
  label: z.string(),
});

const hubspotEntityReferenceObjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('HUBSPOT_ENTITY_REFERENCE'),
  newValue: hubspotEntityReferenceSchema.nullable(),
  oldValue: hubspotEntityReferenceSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

export type HubspotEntityReferenceObject = z.infer<
  typeof hubspotEntityReferenceObjectSchema
>;

const hubspotPropertyMappingObjectTypes = z.enum([
  'DEAL',
  'LINE_ITEM',
  'COMPANY',
]);

export type HubspotPropertyMappingObjectType = z.infer<
  typeof hubspotPropertyMappingObjectTypes
>;

const hubspotPropertyMappingSchema = z.object({
  id: z.string(),
  label: z.string(),
  pages: z.array(z.string()),
  value: z.string().nullable(),
  valueLabel: z.string().nullable(),
  propertyObjectTypeId: hubspotPropertyMappingObjectTypes,
});

export type HubspotPropertyMapping = z.infer<
  typeof hubspotPropertyMappingSchema
>;

const hubspotPropertyMappingListSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('HUBSPOT_PROPERTY_MAPPING_LIST'),
  newValue: z.array(hubspotPropertyMappingSchema).catch([]),
  oldValue: z.array(hubspotPropertyMappingSchema).catch([]),
  redacted: z.boolean().optional(),
});

export type HubspotPropertyMappingList = z.infer<
  typeof hubspotPropertyMappingListSchema
>;

const hubspotPipelineChangeSchema = z.object({
  pipelineId: z.string(),
  pipelineLabel: z.string(),
  pipelineStageId: z.string(),
  pipelineStageLabel: z.string(),
  enabled: z.boolean(),
  startDate: z.string().nullable(),
  lastSyncAt: z.string().nullable(),
});

const hubspotPipelineDeltaSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_HUBSPOT_PIPELINE_DELTA'),
  newValue: createDeltaSchema(hubspotPipelineChangeSchema).nullable(),
  oldValue: createDeltaSchema(hubspotPipelineChangeSchema)
    .nullable()
    .optional(),
  redacted: z.boolean().optional(),
});

export type HubspotPipelineChange = z.infer<typeof hubspotPipelineChangeSchema>;
export type HubspotPipelineDelta = z.infer<typeof hubspotPipelineDeltaSchema>;

const fileListSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('FILE_LIST'),
  newValue: z.array(fileSchema).catch([]),
  oldValue: z.array(fileSchema).catch([]),
  redacted: z.boolean().optional(),
});

export type FileList = z.infer<typeof fileListSchema>;

const projectCostSchema = z.object({
  id: z.string(),
  key: z.string(),
  amount: auditCurrencyValueSchema.nullable(),
  description: z.string().nullable(),
  addedAt: dateTimeSchema.nullable(),
});

const projectCostListSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('PROJECT_COST_LIST'),
  newValue: z.array(projectCostSchema).catch([]),
  oldValue: z.array(projectCostSchema).catch([]),
  redacted: z.boolean().optional(),
});

export type ProjectCostList = z.infer<typeof projectCostListSchema>;

const noteSchema = z.object({
  id: z.string(),
  key: z.string(),
  content: z.string().nullable(),
});

const noteListSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('NOTE_LIST'),
  newValue: z.array(noteSchema).catch([]).nullable().optional(),
  oldValue: z.array(noteSchema).catch([]).nullable().optional(),
  redacted: z.boolean().optional(),
});

export type NoteList = z.infer<typeof noteListSchema>;

const urlLinkSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string().nullable(),
  url: z.string(),
});

const urlLinkListSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('URL_LINK_LIST'),
  newValue: z.array(urlLinkSchema).catch([]).nullable().optional(),
  oldValue: z.array(urlLinkSchema).catch([]).nullable().optional(),
  redacted: z.boolean().optional(),
});

export type UrlLinkList = z.infer<typeof urlLinkListSchema>;

const cachedEmployeeSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  email: z.string(),
});

const cachedProjectCostTimeSchema = z.object({
  rate: auditCurrencyValueSchema.nullable(),
  hours: z.number({ coerce: true }),
  employee: cachedEmployeeSchema,
});

const projectCostTimeSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('TIME_COST'),
  newValue: cachedProjectCostTimeSchema.nullable().optional(),
  oldValue: cachedProjectCostTimeSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

export type ProjectCostTime = z.infer<typeof projectCostTimeSchema>;

const cachedProjectFileSchema = cachedFileSchema.extend({
  type: z.literal('CACHED_PROJECT_FILE'),
});

const cachedSparseEntityDeltaSchema = createDeltaSchema(sparseEntitySchema);

const cachedEntityDeltaListSchema = z.object({
  id: z.string(),
  name: z.string(),
  newValue: cachedSparseEntityDeltaSchema.nullable(),
  oldValue: cachedSparseEntityDeltaSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

export type CachedEntityDeltaList = z.infer<typeof cachedEntityDeltaListSchema>;

const cachedProjectDeltaListSchema = cachedEntityDeltaListSchema.extend({
  type: z.literal('CACHED_PROJECT_DELTA_LIST'),
});

const cachedAssetDeltaListSchema = cachedEntityDeltaListSchema.extend({
  type: z.literal('CACHED_ASSET_DELTA_LIST'),
});

const cachedTicketFileSchema = cachedFileSchema.extend({
  type: z.literal('CACHED_TICKET_FILE'),
});

const cachedBillingPeriodSchema = createDeltaSchema(
  z.object({
    id: z.string(),
    date: dateTimeSchema,
    isPaid: z.boolean(),
    isBilled: z.boolean(),
  })
);

const cachedProjectBillingPeriodSchema = z.object({
  periods: cachedBillingPeriodSchema,
  productLineItemId: z.string(),
  productLineItemKey: z.string(),
  quantity: z.number(),
  unitPrice: auditCurrencyValueSchema.nullable(),
  billingFrequency: z.string().nullable(),
  item: sparseEntitySchema.nullish(),
});

const cachedProjectItemBillingDeltaListPropertySchema = createPropertySchema(
  z.array(cachedProjectBillingPeriodSchema),
  'CACHED_ITEM_BILLING_DELTA_LIST'
);
export type CachedProjectItemBillingDeltaListProperty = z.infer<
  typeof cachedProjectItemBillingDeltaListPropertySchema
>;

const cachedBillingDeltaListPropertySchema = createPropertySchema(
  cachedBillingPeriodSchema,
  'CACHED_BILLING_DELTA_LIST'
);
export type CachedBillingDeltaListProperty = z.infer<
  typeof cachedBillingDeltaListPropertySchema
>;

const cachedBomItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  qty: z.number(),
  sku: z.string().nullable(),
  name: z.string(),
});

const cachedBomDeltaListPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_ITEM_BOM_DELTA_LIST'),
  newValue: createDeltaSchema(cachedBomItemSchema).nullable(),
  oldValue: createDeltaSchema(cachedBomItemSchema).nullable().optional(),
  redacted: z.boolean().optional(),
});

export type CachedBomDeltaListProperty = z.infer<
  typeof cachedBomDeltaListPropertySchema
>;

const cachedBundleMemberSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  sku: z.string().nullable(),
  qty: z.number(),
  position: z.number(),
});

const cachedBundleMembersDeltaListPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_BUNDLE_MEMBERS_DELTA_LIST'),
  newValue: createDeltaSchema(cachedBundleMemberSchema).nullable(),
  oldValue: createDeltaSchema(cachedBundleMemberSchema).nullable().optional(),
  redacted: z.boolean().optional(),
});

export type CachedBundleMembersDeltaListProperty = z.infer<
  typeof cachedBundleMembersDeltaListPropertySchema
>;

const cachedChargeItemSchema = z.object({
  label: z.string(),
  pricingMode: z.string(),
  quantity: z.number(),
  unitPrice: auditCurrencyValueSchema.nullable(),
  totalPrice: auditCurrencyValueSchema,
  servicePeriodStart: z.string().nullable(),
  servicePeriodEnd: z.string().nullable(),
  expectedBillableDate: z.string().nullable(),
  billingEligibleDate: z.string().nullable(),
  status: z.string(),
});

const cachedChargeDeltaPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_CHARGE_DELTA'),
  newValue: createDeltaSchema(cachedChargeItemSchema).nullable(),
  oldValue: createDeltaSchema(cachedChargeItemSchema).nullable().optional(),
  redacted: z.boolean().optional(),
});

export type CachedChargeDeltaProperty = z.infer<
  typeof cachedChargeDeltaPropertySchema
>;

const cachedChargeCascadeEntrySchema = z.object({
  chargeContext: z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
  }),
  projectLineItemContext: z.object({
    id: z.string(),
    key: z.string(),
  }),
  itemContext: z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    sku: z.string(),
  }),
  // PricingMode + unitPrice were added after CACHED_CHARGE_CASCADE_LIST
  // properties started being emitted. Legacy audit rows in the database
  // don't have them, so both are optional — when absent the FE hides the
  // "@ <price>/unit" segment entirely rather than guessing a default.
  pricingMode: z.string().optional(),
  quantity: z.number(),
  unitPrice: auditCurrencyValueSchema.nullable().optional(),
  totalPrice: auditCurrencyValueSchema,
  expectedBillableDate: z.string().nullable(),
});

const cachedChargeCascadeListPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_CHARGE_CASCADE_LIST'),
  newValue: z.array(cachedChargeCascadeEntrySchema).nullable(),
  oldValue: z.array(cachedChargeCascadeEntrySchema).nullable().optional(),
  redacted: z.boolean().optional(),
});

export type CachedChargeCascadeListProperty = z.infer<
  typeof cachedChargeCascadeListPropertySchema
>;

const cachedInvoiceLineItemDeltaEntrySchema = z.object({
  lineItemKey: z.string(),
  indexPosition: z.number(),
  chargeContext: z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
  }),
  projectLineItemContext: z.object({
    id: z.string(),
    key: z.string(),
  }),
  itemContext: z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    sku: z.string(),
  }),
  description: z.string(),
  pricingMode: z.string(),
  quantity: z.number(),
  unitPrice: auditCurrencyValueSchema.nullable(),
  taxAmount: auditCurrencyValueSchema.nullable(),
  totalPrice: auditCurrencyValueSchema,
  servicePeriodStart: z.string().nullable(),
  servicePeriodEnd: z.string().nullable(),
});

const cachedInvoiceLineItemDeltaListPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_INVOICE_LINE_ITEM_DELTA_LIST'),
  newValue: z.array(cachedInvoiceLineItemDeltaEntrySchema).nullable(),
  oldValue: z
    .array(cachedInvoiceLineItemDeltaEntrySchema)
    .nullable()
    .optional(),
  redacted: z.boolean().optional(),
});

export type CachedInvoiceLineItemDeltaListProperty = z.infer<
  typeof cachedInvoiceLineItemDeltaListPropertySchema
>;

const cachedInvoiceContextPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_INVOICE_CONTEXT'),
  newValue: z
    .object({
      id: z.string(),
      key: z.string(),
      invoiceNumber: z.string(),
    })
    .nullable(),
  oldValue: z
    .object({
      id: z.string(),
      key: z.string(),
      invoiceNumber: z.string(),
    })
    .nullable()
    .optional(),
  redacted: z.boolean().optional(),
});

const cachedInvoiceReferenceValueSchema = z.object({
  invoice: z.object({
    id: z.string(),
    key: z.string(),
    invoiceNumber: z.string(),
  }),
  charges: z
    .array(
      z.object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
      })
    )
    .nullable()
    .transform((v) => v ?? []),
});

const cachedInvoiceReferencePropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_INVOICE_REFERENCE'),
  newValue: cachedInvoiceReferenceValueSchema.nullable(),
  oldValue: cachedInvoiceReferenceValueSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

const cachedCustomerMergeItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  comment: z.string().nullable(),
  externalId: z.string().nullable(),
  billingAddress: z.string().nullable(),
  billingContactName: z.string().nullable(),
  billingContactEmail: z.string().nullable(),
  billingContactPhone: z.string().nullable(),
  isCustomer: z.boolean(),
  isSupplier: z.boolean(),
  // Optional because merge events recorded before archived state joined the
  // merge selection do not carry it
  isArchived: z.boolean().optional(),
});

export type CachedCustomerMergeItem = z.infer<
  typeof cachedCustomerMergeItemSchema
>;

const cachedCustomerMergeEntrySchema = z.object({
  merged: cachedCustomerMergeItemSchema,
  retained: cachedCustomerMergeItemSchema,
  removed: cachedCustomerMergeItemSchema,
});

const cachedCustomerMergeSchema = createPropertySchema(
  cachedCustomerMergeEntrySchema,
  'CACHED_CUSTOMER_MERGE'
);
export type CachedCustomerMerge = z.infer<typeof cachedCustomerMergeSchema>;

const cachedContactMergeItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  title: z.string().nullable(),
  comments: z.string().nullable(),
  customerName: z.string(),
  isArchived: z.boolean(),
});

export type CachedContactMergeItem = z.infer<
  typeof cachedContactMergeItemSchema
>;

const cachedContactMergeEntrySchema = z.object({
  merged: cachedContactMergeItemSchema,
  retained: cachedContactMergeItemSchema,
  removed: cachedContactMergeItemSchema,
});

const cachedContactMergeSchema = createPropertySchema(
  cachedContactMergeEntrySchema,
  'CACHED_CONTACT_MERGE'
);
export type CachedContactMerge = z.infer<typeof cachedContactMergeSchema>;

const cachedLocationMergeItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  // Entries recorded before zones could be merged carry no type and are sites
  type: locationTypeSchema.nullish().transform((value) => value ?? 'SITE'),
  address: z.string().nullable(),
  // A zone has no transience and computes its inventory from the parent site
  isInventory: z.boolean().nullable(),
  isTransient: z.boolean().nullable(),
  assignedCustomer: sparseEntitySchema.nullable(),
  description: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  consignee: sparseEntitySchema.nullish().transform((value) => value ?? null),
});

export type CachedLocationMergeItem = z.infer<
  typeof cachedLocationMergeItemSchema
>;

const cachedLocationMergeEntrySchema = z.object({
  merged: cachedLocationMergeItemSchema,
  retained: cachedLocationMergeItemSchema,
  removed: cachedLocationMergeItemSchema,
});

const cachedLocationMergeSchema = createPropertySchema(
  cachedLocationMergeEntrySchema,
  'CACHED_LOCATION_MERGE'
);
export type CachedLocationMerge = z.infer<typeof cachedLocationMergeSchema>;

const mergedItemFieldSchema = z.object({
  name: z.string(),
  type: z.string(),
  mergeType: z.string(),
});

const cachedItemMergeSchema = createPropertySchema(
  z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    sku: z.string().nullable(),
    nonInventory: z.boolean(),
    description: z.string().nullable(),
    fields: z.array(mergedItemFieldSchema).optional(),
  }),
  'CACHED_ITEM_MERGE'
);

export type CachedItemMerge = z.infer<typeof cachedItemMergeSchema>;

const cachedWorkflowSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
});

const cachedWorkflowStageSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  orderIndex: z.number(),
  workflow: cachedWorkflowSchema,
});

const cachedWorkflowSelectionPropertySchema = createPropertySchema(
  cachedWorkflowStageSchema,
  'CACHED_WORKFLOW_SELECTION'
);

export type CachedWorkflowSelectionProperty = z.infer<
  typeof cachedWorkflowSelectionPropertySchema
>;

const cachedWorkflowStageItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  orderIndex: z.number(),
  description: z.string().nullable(),
});

const cachedWorkflowStageListPropertySchema = createPropertySchema(
  z.array(cachedWorkflowStageItemSchema),
  'CACHED_WORKFLOW_STAGE_LIST'
);

export type CachedWorkflowStageListProperty = z.infer<
  typeof cachedWorkflowStageListPropertySchema
>;

const cachedWorkflowStageCascadeItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  orderIndex: z.number(),
  description: z.string().nullish(),
  shouldHighlight: z.boolean(),
});

const cachedWorkflowStageCascadeListPropertySchema = createPropertySchema(
  z.array(cachedWorkflowStageCascadeItemSchema),
  'CACHED_WORKFLOW_STAGE_CASCADE_LIST'
);

export type CachedWorkflowStageCascadeListProperty = z.infer<
  typeof cachedWorkflowStageCascadeListPropertySchema
>;

const cachedLedgerAccountItemSchema = z.object({
  accountType: ledgerAccountTypeSchema,
  ledgerAccountName: z.string(),
});

const cachedLedgerAccountDeltaSchema = createDeltaSchema(
  cachedLedgerAccountItemSchema
);

const cachedLedgerAccountListPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_LEDGER_ACCOUNT_LIST'),
  newValue: cachedLedgerAccountDeltaSchema.nullable(),
  oldValue: cachedLedgerAccountDeltaSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

export type CachedLedgerAccountListProperty = z.infer<
  typeof cachedLedgerAccountListPropertySchema
>;

const cachedAttentionThresholdItemSchema = z.object({
  entity: attentionThresholdEntitySchema,
  reason: attentionReasonSchema,
  noticeDays: z.number(),
  warningDays: z.number(),
  criticalDays: z.number(),
});

const cachedAttentionThresholdDeltaSchema = createDeltaSchema(
  cachedAttentionThresholdItemSchema
);

const cachedAttentionThresholdListPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_ATTENTION_THRESHOLD_LIST'),
  newValue: cachedAttentionThresholdDeltaSchema.nullable(),
  oldValue: cachedAttentionThresholdDeltaSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

export type CachedAttentionThresholdItem = z.infer<
  typeof cachedAttentionThresholdItemSchema
>;

export type CachedAttentionThresholdListProperty = z.infer<
  typeof cachedAttentionThresholdListPropertySchema
>;

const cachedSerialPatternItemSchema = z.object({
  expression: z.string(),
  // Null when no picker kind produces the expression.
  kind: serialPatternKindSchema.nullable(),
  length: z.number().nullable(),
  prefix: z.string().nullable(),
});

const cachedSerialPatternDeltaSchema = createDeltaSchema(
  cachedSerialPatternItemSchema
);

const cachedSerialPatternDeltaListPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_SERIAL_PATTERN_DELTA_LIST'),
  newValue: cachedSerialPatternDeltaSchema.nullable(),
  oldValue: cachedSerialPatternDeltaSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

export type CachedSerialPatternItem = z.infer<
  typeof cachedSerialPatternItemSchema
>;

export type CachedSerialPatternDeltaListProperty = z.infer<
  typeof cachedSerialPatternDeltaListPropertySchema
>;

const cachedOAuthScopeDeltaListPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_OAUTH_SCOPE_DELTA_LIST'),
  newValue: createDeltaSchema(oauthScopeSchema).nullable(),
  oldValue: createDeltaSchema(oauthScopeSchema).nullable().optional(),
  redacted: z.boolean().optional(),
});

export type CachedOAuthScopeDeltaListProperty = z.infer<
  typeof cachedOAuthScopeDeltaListPropertySchema
>;

const integrationStatusSchema = z.object({
  name: z.string(),
  status: z.string(),
});

const integrationStatusPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('INTEGRATION_STATUS'),
  newValue: integrationStatusSchema.nullable(),
  oldValue: integrationStatusSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

const remoteIntegrationItemValueSchema = z.object({
  remoteId: z.string(),
  integrationType: z.string(),
  name: z.string(),
  sku: z.string(),
});

const remoteIntegrationItemPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('REMOTE_INTEGRATION_ITEM'),
  newValue: remoteIntegrationItemValueSchema.nullable(),
  oldValue: remoteIntegrationItemValueSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

const carrierTrackingPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CARRIER_TRACKING'),
  newValue: eventPropertyCarrierTrackingValueSchema.nullable(),
  oldValue: eventPropertyCarrierTrackingValueSchema.optional().nullable(),
  redacted: z.boolean().optional(),
});

export type AuditCarrierTrackingProperty = z.infer<
  typeof carrierTrackingPropertySchema
>;

const transportReferenceValueSchema = z.object({
  // An unknown carrier code degrades to null rather than dropping the property.
  carrier: shippingCarrierSchema.nullable().catch(null),
  name: z.string().nullable(),
  transportReference: z.string().nullable(),
});

const transportReferencePropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('TRANSPORT_REFERENCE'),
  newValue: transportReferenceValueSchema.nullable(),
  oldValue: transportReferenceValueSchema.optional().nullable(),
  redacted: z.boolean().optional(),
});

export type AuditTransportReferenceProperty = z.infer<
  typeof transportReferencePropertySchema
>;

const fulfillmentOrderPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_FULFILLMENT_ORDER'),
  newValue: fulfillmentOrderSchema.nullable(),
  oldValue: fulfillmentOrderSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

export type AuditFulfillmentOrderProperty = z.infer<
  typeof fulfillmentOrderPropertySchema
>;

const returnOrderPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_RETURN_ORDER'),
  newValue: returnOrderSchema.nullable(),
  oldValue: returnOrderSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

export type AuditReturnOrderProperty = z.infer<
  typeof returnOrderPropertySchema
>;

// A purchase order reference is normally the sparse object, but the property
// tolerates a bare number string: rows written before the field carried an object
// keep whatever shape they were written with, since a property's type and value
// are frozen into the audit row. A string renders as plain text.
const purchaseOrderReferenceValueSchema = purchaseOrderSchema.or(z.string());

const purchaseOrderPropertySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('CACHED_PURCHASE_ORDER'),
  newValue: purchaseOrderReferenceValueSchema.nullable(),
  oldValue: purchaseOrderReferenceValueSchema.nullable().optional(),
  redacted: z.boolean().optional(),
});

export type AuditPurchaseOrderProperty = z.infer<
  typeof purchaseOrderPropertySchema
>;

const shipmentManifestEntrySchema = z.object({
  item: z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    sku: z.string().nullish(),
    type: z.string().nullish(),
  }),
  quantity: z.number().nullable(),
  receivedQuantity: z.number().nullable(),
  externalReference: z.string().nullable(),
  // What the line is worth, so a value change reads against the line that moved
  // rather than as a shipment-wide figure. Normalizes to a formatted string.
  // Optional, not just nullable: manifest audits written before the line carried
  // a value have no such key, and a required key would drop the whole property.
  value: auditCurrencyValueSchema.nullish().transform((v) => v ?? null),
  // Absent on audits recorded before the whole serial list was captured.
  serialFileId: z.string().nullish(),
  serialFileKey: z.string().nullish(),
  numberSerials: z.number().nullish(),
  addedFileId: z.string().nullable(),
  addedFileKey: z.string().nullable(),
  numberAdded: z.number().nullable(),
  removedFileId: z.string().nullable(),
  removedFileKey: z.string().nullable(),
  numberRemoved: z.number().nullable(),
});

const shipmentManifestSchema = z.array(shipmentManifestEntrySchema);

export type ShipmentManifestEntry = z.infer<typeof shipmentManifestEntrySchema>;

const cachedShipmentManifestPropertySchema = createPropertySchema(
  shipmentManifestSchema,
  'CACHED_SHIPMENT_MANIFEST'
);

export type ShipmentManifestProperty = z.infer<
  typeof cachedShipmentManifestPropertySchema
>;

const shipmentQuarantineItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  sku: z.string().nullish(),
  type: z.string().nullish(),
});

const shipmentQuarantineEntrySchema = z.object({
  serial: z.string(),
  // Why the serial was held. Optional because entries audited before this field
  // existed carry no value; the label falls back to inferring from the shape.
  quarantineType: quarantineTypeSchema.nullish(),
  reportedItem: shipmentQuarantineItemSchema,
  // The item Hardfin already has the serial under; null for a hold on a serial
  // Hardfin has no record of.
  existingItem: shipmentQuarantineItemSchema.nullable(),
  // The asset the serial already belongs to, carrying its key so the entry can
  // link straight to it. Null alongside existingItem.
  existingAsset: z
    .object({
      id: z.string(),
      key: z.string(),
      serial: z.string(),
    })
    .nullable(),
});

export type ShipmentQuarantineEntry = z.infer<
  typeof shipmentQuarantineEntrySchema
>;

const cachedShipmentQuarantinePropertySchema = createPropertySchema(
  z.array(shipmentQuarantineEntrySchema),
  'CACHED_SHIPMENT_QUARANTINE'
);

export type ShipmentQuarantineProperty = z.infer<
  typeof cachedShipmentQuarantinePropertySchema
>;

const shipmentQuarantineCorrectionSideSchema = z.object({
  asset: z
    .object({ id: z.string(), key: z.string(), serial: z.string() })
    .nullable(),
  item: shipmentQuarantineItemSchema,
});

const shipmentQuarantineResolutionSchema = z.object({
  serial: z.string(),
  kind: quarantineResolutionKindSchema,
  note: z.string(),
  resolvedItem: shipmentQuarantineItemSchema,
  // The unit that actually moved — different from the held serial for a serial correction.
  resolvedAsset: z
    .object({
      id: z.string(),
      key: z.string(),
      serial: z.string(),
    })
    .nullable(),
  // The before/after when the resolution changed the serial or the item.
  correction: z
    .object({
      old: shipmentQuarantineCorrectionSideSchema,
      new: shipmentQuarantineCorrectionSideSchema,
    })
    .nullable(),
});

export type ShipmentQuarantineResolution = z.infer<
  typeof shipmentQuarantineResolutionSchema
>;

const cachedShipmentQuarantineResolutionPropertySchema = createPropertySchema(
  shipmentQuarantineResolutionSchema,
  'CACHED_SHIPMENT_QUARANTINE_RESOLUTION'
);

export type ShipmentQuarantineResolutionProperty = z.infer<
  typeof cachedShipmentQuarantineResolutionPropertySchema
>;
// A fulfillment order line carries only the quantity the order asked for. The
// pending/shipped/delivered/quarantined tiers are recomputed from shipments and
// belong to the shipment's audit trail.
const fulfillmentManifestEntrySchema = z.object({
  item: z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    sku: z.string().nullish(),
    type: z.string().nullish(),
  }),
  requestedQuantity: z.number().nullable(),
  // Populated only on the shipment-cascade sync audit; null on order-authored
  // manifests. These are what a shipment moved.
  pendingQuantity: z.number().nullish(),
  shippedQuantity: z.number().nullish(),
  deliveredQuantity: z.number().nullish(),
  quarantinedQuantity: z.number().nullish(),
  // Present only on the FULFILLMENT_ORDER_3PL send audit; captured for forensics
  // and intentionally not rendered.
  thirdPartyBinding: z
    .object({
      remoteOrderId: z.string(),
      remoteProductId: z.string(),
    })
    .nullish(),
});

const fulfillmentManifestSchema = z.array(fulfillmentManifestEntrySchema);

export type FulfillmentManifestEntry = z.infer<
  typeof fulfillmentManifestEntrySchema
>;

// A purchase order line carries the quantity the order asked for plus the tiers
// recomputed from shipments.
const purchaseOrderManifestEntrySchema = z.object({
  item: z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    sku: z.string().nullish(),
    type: z.string().nullish(),
  }),
  requestedQuantity: z.number().nullable(),
  // Populated only on the shipment-cascade sync audit; null on order-authored
  // manifests. These are what a shipment moved.
  openQuantity: z.number().nullish(),
  shippedQuantity: z.number().nullish(),
  deliveredQuantity: z.number().nullish(),
  receivedQuantity: z.number().nullish(),
  quarantinedQuantity: z.number().nullish(),
});

const purchaseOrderManifestSchema = z.array(purchaseOrderManifestEntrySchema);

export type PurchaseOrderManifestEntry = z.infer<
  typeof purchaseOrderManifestEntrySchema
>;

const cachedPurchaseOrderManifestPropertySchema = createPropertySchema(
  purchaseOrderManifestSchema,
  'CACHED_PURCHASE_ORDER_MANIFEST'
);

export type PurchaseOrderManifestProperty = z.infer<
  typeof cachedPurchaseOrderManifestPropertySchema
>;

// A return order line carries the quantity the order asked for plus the tiers
// recomputed from shipments.
const returnOrderManifestEntrySchema = z.object({
  item: z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    sku: z.string().nullish(),
    type: z.string().nullish(),
  }),
  requestedQuantity: z.number().nullable(),
  // Populated only on the shipment-cascade sync audit; null on order-authored
  // manifests. These are what a shipment moved.
  openQuantity: z.number().nullish(),
  shippedQuantity: z.number().nullish(),
  deliveredQuantity: z.number().nullish(),
  receivedQuantity: z.number().nullish(),
  quarantinedQuantity: z.number().nullish(),
});

const returnOrderManifestSchema = z.array(returnOrderManifestEntrySchema);

export type ReturnOrderManifestEntry = z.infer<
  typeof returnOrderManifestEntrySchema
>;

const cachedReturnOrderManifestPropertySchema = createPropertySchema(
  returnOrderManifestSchema,
  'CACHED_RETURN_ORDER_MANIFEST'
);

export type ReturnOrderManifestProperty = z.infer<
  typeof cachedReturnOrderManifestPropertySchema
>;

const cachedFulfillmentManifestPropertySchema = createPropertySchema(
  fulfillmentManifestSchema,
  'CACHED_FULFILLMENT_MANIFEST'
);

export type FulfillmentManifestProperty = z.infer<
  typeof cachedFulfillmentManifestPropertySchema
>;

// Enum values whose abbreviated tail does not read as a word once sentence-cased.
// The table is keyed by the raw value with no entity context, so a status that
// several order kinds share can only render one way. The maps disagree on
// OPEN_PARTIAL — a fulfillment order treats open as a modifier phase and purchase
// and return orders do not — so the purchase and return order maps go first and
// the fulfillment map keeps every key it already owned. The purchase and return
// order maps carry identical labels and contribute only the RECEIVED_* states,
// which no fulfillment order has and which previously fell through to the
// generic formatter.
const TEXT_ENUM_DISPLAY_OVERRIDES: Record<string, string> = {
  ...PO_STATUS_AUDIT_LABELS,
  ...RO_STATUS_AUDIT_LABELS,
  ...FO_STATUS_AUDIT_LABELS,
  ...FO_CLOSE_SOURCE_AUDIT_LABELS,
};

function formatTextEnumValue(value: string): string {
  return (
    TEXT_ENUM_DISPLAY_OVERRIDES[value] ??
    (value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()).replace(
      /_/g,
      ' '
    )
  );
}

export const auditContextPropertySchema = z
  .discriminatedUnion('type', [
    z.object({
      id: z.string(),
      type: z.literal('SYSTEM_ENUM'),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.enum(['TEXT', 'TEXT_ENUM']),
      newValue: z.string().nullable(),
      oldValue: z.string().nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('BOOLEAN'),
      newValue: z.boolean().nullable(),
      oldValue: z.boolean().nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('INTEGER'),
      newValue: z.number().nullable(),
      oldValue: z.number().nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('DURATION_MINUTES'),
      newValue: z.number().nullable(),
      oldValue: z.number().nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('NUMERIC'),
      newValue: z.string().nullable(),
      oldValue: z.string().nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('CURRENCY'),
      newValue: auditCurrencyValueSchema.nullable(),
      oldValue: auditCurrencyValueSchema.nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('FILE'),
      newValue: fileSchema.nullable(),
      oldValue: fileSchema.nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('DATE_TIME'),
      newValue: dateTimeSchema.nullable(),
      oldValue: dateTimeSchema.optional().nullable(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('DATE'),
      newValue: dateSchema.nullable(),
      oldValue: dateSchema.optional().nullable(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('PAYMENT_TERM'),
      newValue: paymentTermSchema.nullable(),
      oldValue: paymentTermSchema.optional().nullable(),
      redacted: z.boolean().optional(),
    }),
    carrierTrackingPropertySchema,
    transportReferencePropertySchema,
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('ASSET_FUNCTIONAL_STATUS'),
      newValue: assetFunctionalStatusValueSchema,
      oldValue: assetFunctionalStatusValueSchema.optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('DEPRECIATION_MODEL'),
      newValue: depreciationModelValueSchema.nullable(),
      oldValue: depreciationModelValueSchema.nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('SITE_METADATA'),
      newValue: siteMetadataSchema.nullable(),
      oldValue: siteMetadataSchema.nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('CACHED_PERSON'),
      newValue: personSchema.nullable(),
      oldValue: personSchema.nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('CACHED_CUSTOMER'),
      newValue: customerSchema.nullable(),
      oldValue: customerSchema.nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    fulfillmentOrderPropertySchema,
    returnOrderPropertySchema,
    purchaseOrderPropertySchema,
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('CACHED_SHIPMENT'),
      newValue: cachedShipmentSchema.nullable(),
      oldValue: cachedShipmentSchema.nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('CACHED_COMPANY'),
      newValue: companySchema.nullable(),
      oldValue: companySchema.nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('CACHED_ITEM'),
      newValue: itemSchema.nullable(),
      oldValue: itemSchema.nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('CACHED_PROJECT'),
      newValue: projectSchema.nullable(),
      oldValue: projectSchema.nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('CACHED_ASSET_LIST'),
      newValue: z.array(assetAssignmentSchema),
      oldValue: z.array(assetAssignmentSchema).optional(),
      redacted: z.boolean().optional(),
    }),
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.literal('URL_LINK'),
      newValue: urlLinkSchema.nullable(),
      oldValue: urlLinkSchema.nullable().optional(),
      redacted: z.boolean().optional(),
    }),
    projectItemListSchema,
    projectLineItemsListSchema,
    locationListSchema,
    locationPropertySchema,
    assetAssignmentListSchema,
    assetMetadataSchema,
    assetMetadataListSchema,
    auditContextItemFieldTypeSchema,
    auditContactSchema,
    notificationConfigurationListSchema,
    hubspotEntityReferenceObjectSchema,
    hubspotPropertyMappingListSchema,
    hubspotPipelineDeltaSchema,
    fileListSchema,
    urlLinkListSchema,
    projectCostListSchema,
    noteListSchema,
    projectCostTimeSchema,
    cachedProjectFileSchema,
    cachedProjectDeltaListSchema,
    cachedAssetDeltaListSchema,
    cachedTicketFileSchema,
    cachedProjectItemBillingDeltaListPropertySchema,
    cachedBillingDeltaListPropertySchema,
    cachedBomDeltaListPropertySchema,
    cachedBundleMembersDeltaListPropertySchema,
    cachedChargeDeltaPropertySchema,
    cachedChargeCascadeListPropertySchema,
    cachedInvoiceContextPropertySchema,
    cachedInvoiceLineItemDeltaListPropertySchema,
    cachedInvoiceReferencePropertySchema,
    cachedCustomerMergeSchema,
    cachedContactMergeSchema,
    cachedLocationMergeSchema,
    cachedItemMergeSchema,
    cachedWorkflowSelectionPropertySchema,
    cachedWorkflowStageListPropertySchema,
    cachedWorkflowStageCascadeListPropertySchema,
    cachedLedgerAccountListPropertySchema,
    cachedAttentionThresholdListPropertySchema,
    cachedSerialPatternDeltaListPropertySchema,
    cachedOAuthScopeDeltaListPropertySchema,
    integrationStatusPropertySchema,
    remoteIntegrationItemPropertySchema,
    cachedShipmentManifestPropertySchema,
    cachedShipmentQuarantinePropertySchema,
    cachedShipmentQuarantineResolutionPropertySchema,
    cachedFulfillmentManifestPropertySchema,
    cachedPurchaseOrderManifestPropertySchema,
    cachedReturnOrderManifestPropertySchema,
  ])
  .transform((property) => {
    if (property.type === 'TEXT_ENUM') {
      if (property.newValue) {
        property.newValue = formatTextEnumValue(property.newValue);
      }
      if (property.oldValue) {
        property.oldValue = formatTextEnumValue(property.oldValue);
      }
    }
    // Unfortunately, the site's address is nested in a "metadata" object and
    // the API does not flatten the metadata properties. This is not something
    // that is user facing, so the property is renamed to "Address" here.
    if (property.type === 'SITE_METADATA') {
      return { ...property, name: 'Address' };
    }

    return property;
  });

export type AuditContextProperty = z.infer<typeof auditContextPropertySchema>;

/**
 * Schema for parsing audit context properties.
 *
 * Uses a for-loop instead of reduce with spread to avoid O(n²) complexity
 * when parsing events with many properties.
 */
const auditContextPropertiesSchema = z
  // eslint-disable-next-line no-restricted-syntax -- Backend context is json.RawMessage (polymorphic). Parse each tolerantly and filter invalid.
  .array(z.unknown())
  .nullish()
  .transform((properties) => {
    if (properties === null || properties === undefined) {
      return [];
    }

    const validProperties: AuditContextProperty[] = [];

    for (const property of properties) {
      const result = auditContextPropertySchema.safeParse(property);

      if (result.success) {
        validProperties.push(result.data);
      } else {
        const error = new Error('Invalid audit context property', {
          cause: property,
        });

        if (import.meta.env.MODE === 'development') {
          console.error('Invalid audit event:', property, result.error);
        }

        Sentry.captureException(error, { extra: { property } });
      }
    }

    return validProperties;
  });

interface DateTimeProperty {
  id: 'occurred-at';
  name: 'Date';
  type: 'DATE_TIME';
  newValue: DateTime;
  redacted?: boolean;
}

/**
 * Important note about the `eventTargetObject` attribute that's below
 *
 * It is used to generate links to the entity in the organization's main audit log.
 * It MUST be filled out if you want it to be linked correctly.
 * See https://github.com/hardfinhq/hardfin/pull/12054
 */

const assetAuditEventContextSchema = z
  .discriminatedUnion('type', [
    // Asset event type
    baseAuditEventContextSchema.extend({
      type: z.literal('ASSET_EVENT'),
      occurredAt: dateTimeSchema.nullable(),
      assetContext: sparseEntitySchema,
      eventTypeName: z.string(),
      properties: auditContextPropertiesSchema,
    }),

    // Asset and project assignments type
    baseAuditEventContextSchema.extend({
      type: z.enum([
        'ASSET',
        'ASSET_UPDATE',
        'ASSET_CREATE',
        'ASSET_FILE',
        'ASSET_COST_ADJUSTMENT',
        'ASSET_USEFUL_LIFE_REVISION',
        'ASSET_URL_LINK',
        'ASSET_SCRAP',
        'ASSET_UNSCRAP',
        'ASSET_METADATA_FIELD',
        'ASSET_PROJECT_ASSIGNMENT',
        'ASSET_PROJECT_UNASSIGNMENT',
        'ASSET_OWNERSHIP_CHANGE',
        'ASSET_LOCATION_CHANGE',
      ]),
      properties: auditContextPropertiesSchema,
      assetContext: sparseEntitySchema,
    }),

    // Asset ticket assignments type
    baseAuditEventContextSchema.extend({
      type: z.enum(['ASSET_TICKET_ASSIGNMENT', 'ASSET_TICKET_UNASSIGNMENT']),
      properties: auditContextPropertiesSchema,
      assetContext: sparseEntitySchema,
      ticketContext: sparseEntitySchema,
    }),
  ])
  .transform((data) => {
    // Handle the ASSET_EVENT specific transform
    if (data.type === 'ASSET_EVENT') {
      const { eventTypeName, occurredAt, properties, ...context } = data;
      const dateTimeProperty: DateTimeProperty[] = occurredAt
        ? [
            {
              id: 'occurred-at',
              name: 'Date',
              type: 'DATE_TIME' as const,
              newValue: occurredAt,
            },
          ]
        : [];

      return {
        ...context,
        name: eventTypeName,
        properties: [...dateTimeProperty, ...properties],
      };
    }

    // Return data as-is for other types
    return data;
  })
  .transform(({ userContext, assetContext, contextType, ...context }) => ({
    ...context,
    actor: userContext,
    asset: assetContext,
    action: contextType,
    eventTargetObject: {
      ...assetContext,
      typeName: 'Asset' as AuditEventTargetObjectTypeName,
    },
  }));

const baseProjectAuditEventContextSchema = baseAuditEventContextSchema.extend({
  properties: auditContextPropertiesSchema,
  projectContext: sparseEntitySchema,
});

const projectAuditEventContextSchema = z
  .discriminatedUnion('type', [
    baseProjectAuditEventContextSchema.extend({
      type: z.enum([
        'PROJECT',
        'PROJECT_FILE',
        'PROJECT_URL_LINK',
        'PROJECT_COST',
        'PROJECT_NOTE',
        'PROJECT_ASSET',
        'PROJECT_ASSET_UNASSIGNMENT',
      ]),
    }),
    baseProjectAuditEventContextSchema.extend({
      type: z.enum([
        'PROJECT_TICKET_ASSIGNMENT',
        'PROJECT_TICKET_UNASSIGNMENT',
      ]),
      ticketContext: sparseEntitySchema,
    }),
    baseProjectAuditEventContextSchema.extend({
      type: z.enum(['PROJECT_BILLING']),
      projectContext: sparseEntitySchema,
      itemContext: sparseEntitySchema.nullish(),
      lineItemQuantity: z.number(),
      lineItemFrequency: z.string().nullable(),
      lineItemUnitPrice: auditCurrencyValueSchema.nullable(),
      lineItemReferenceId: z.string(),
      lineItemReferenceKey: z.string(),
    }),
    baseProjectAuditEventContextSchema.extend({
      type: z.literal('PROJECT_LINE_ITEM_CHARGE_SCHEDULE'),
      lineItemReferenceId: z.string(),
      lineItemReferenceKey: z.string(),
      lineItemContext: z
        .object({
          item: sparseEntitySchema,
          quantity: z.number(),
          location: sparseEntitySchema.nullable(),
          startOn: z.string().nullable(),
          endOn: z.string().nullable(),
          details: z.string().nullable(),
          pricingMode: z.string().nullable(),
          unitPrice: auditCurrencyValueSchema.nullable(),
          fixedPrice: auditCurrencyValueSchema.nullable(),
          chargeSchedule: z.string().nullable(),
          chargeFrequency: z.string().nullable(),
          invoiceMode: z.string().nullable(),
          invoiceFrequency: z.string().nullable(),
        })
        .optional(),
    }),
  ])
  .transform(({ userContext, projectContext, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based on
    // how the properties are used.
    ...context,
    actor: userContext,
    project: projectContext,
    action: contextType,
    eventTargetObject: {
      ...projectContext,
      typeName: 'Project' as AuditEventTargetObjectTypeName,
    },
  }));

const locationAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('LOCATION'),
    properties: auditContextPropertiesSchema,
    locationContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, locationContext, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based on
    // how the properties are used.
    ...context,
    actor: userContext,
    location: locationContext,
    action: contextType,
    eventTargetObject: {
      ...locationContext,
      typeName: 'Location' as AuditEventTargetObjectTypeName,
    },
  }));

const locationZoneAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('LOCATION_ZONE'),
    properties: auditContextPropertiesSchema,
    locationContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    zoneContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(
    ({
      userContext,
      locationContext,
      zoneContext,
      contextType,
      ...context
    }) => ({
      ...context,
      actor: userContext,
      location: locationContext,
      zone: zoneContext,
      action: contextType,
      eventTargetObject: {
        ...zoneContext,
        typeName: 'Location' as AuditEventTargetObjectTypeName,
      },
    })
  );

const costAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('COST'),
    properties: auditContextPropertiesSchema,
    costContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string().optional(),
    }),
  })
  .transform(({ userContext, costContext, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based on
    // how the properties are used.
    ...context,
    actor: userContext,
    cost: costContext,
    action: contextType,
    eventTargetObject: {
      ...costContext,
      name: costContext.name ?? 'Cost',
      typeName: 'Cost' as AuditEventTargetObjectTypeName,
    },
  }));

const shipmentAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('SHIPMENT'),
    operation: z.string().optional(),
    properties: auditContextPropertiesSchema,
    shipmentContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string().optional(),
    }),
  })
  .transform(({ userContext, shipmentContext, contextType, ...context }) => ({
    ...context,
    actor: userContext,
    shipment: shipmentContext,
    action: contextType,
    eventTargetObject: {
      ...shipmentContext,
      name: shipmentContext.name ?? 'Shipment',
      typeName: 'Shipment' as AuditEventTargetObjectTypeName,
    },
  }));

const fulfillmentOrderAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('FULFILLMENT_ORDER'),
    properties: auditContextPropertiesSchema,
    fulfillmentOrderContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string().optional(),
    }),
  })
  .transform(
    ({ userContext, fulfillmentOrderContext, contextType, ...context }) => ({
      ...context,
      actor: userContext,
      fulfillmentOrder: fulfillmentOrderContext,
      action: contextType,
      eventTargetObject: {
        ...fulfillmentOrderContext,
        name: fulfillmentOrderContext.name ?? 'Fulfillment order',
        typeName: 'Fulfillment order' as AuditEventTargetObjectTypeName,
      },
    })
  );

// The 3PL stream shares the fulfillment order's log but is a separate,
// system-attributed entity: user-authored FO changes never cross into it.
const fulfillmentOrderThirdPartyAuditEventContextSchema =
  baseAuditEventContextSchema
    .extend({
      type: z.literal('FULFILLMENT_ORDER_3PL'),
      properties: auditContextPropertiesSchema,
      fulfillmentOrderContext: z.object({
        id: z.string(),
        key: z.string(),
        name: z.string().optional(),
      }),
    })
    .transform(
      ({ userContext, fulfillmentOrderContext, contextType, ...context }) => ({
        ...context,
        actor: userContext,
        fulfillmentOrder: fulfillmentOrderContext,
        action: contextType,
        eventTargetObject: {
          ...fulfillmentOrderContext,
          name: fulfillmentOrderContext.name ?? 'Fulfillment order',
          typeName: 'Fulfillment order' as AuditEventTargetObjectTypeName,
        },
      })
    );

const returnOrderAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('RETURN_ORDER'),
    properties: auditContextPropertiesSchema,
    returnOrderContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string().optional(),
    }),
  })
  .transform(
    ({ userContext, returnOrderContext, contextType, ...context }) => ({
      ...context,
      actor: userContext,
      returnOrder: returnOrderContext,
      action: contextType,
      eventTargetObject: {
        ...returnOrderContext,
        name: returnOrderContext.name ?? 'Return order',
        typeName: 'Return order' as AuditEventTargetObjectTypeName,
      },
    })
  );

// The 3PL stream shares the return order's log but is a separate,
// system-attributed entity: user-authored RO changes never cross into it.
const returnOrderThirdPartyAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('RETURN_ORDER_3PL'),
    properties: auditContextPropertiesSchema,
    returnOrderContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string().optional(),
    }),
  })
  .transform(
    ({ userContext, returnOrderContext, contextType, ...context }) => ({
      ...context,
      actor: userContext,
      returnOrder: returnOrderContext,
      action: contextType,
      eventTargetObject: {
        ...returnOrderContext,
        name: returnOrderContext.name ?? 'Return order',
        typeName: 'Return order' as AuditEventTargetObjectTypeName,
      },
    })
  );

const purchaseOrderAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('PURCHASE_ORDER'),
    properties: auditContextPropertiesSchema,
    purchaseOrderContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string().optional(),
    }),
  })
  .transform(
    ({ userContext, purchaseOrderContext, contextType, ...context }) => ({
      ...context,
      actor: userContext,
      purchaseOrder: purchaseOrderContext,
      action: contextType,
      eventTargetObject: {
        ...purchaseOrderContext,
        name: purchaseOrderContext.name ?? 'Purchase order',
        typeName: 'Purchase order' as AuditEventTargetObjectTypeName,
      },
    })
  );

// The 3PL stream shares the purchase order's log but is a separate,
// system-attributed entity: user-authored PO changes never cross into it.
const purchaseOrderThirdPartyAuditEventContextSchema =
  baseAuditEventContextSchema
    .extend({
      type: z.literal('PURCHASE_ORDER_3PL'),
      properties: auditContextPropertiesSchema,
      purchaseOrderContext: z.object({
        id: z.string(),
        key: z.string(),
        name: z.string().optional(),
      }),
    })
    .transform(
      ({ userContext, purchaseOrderContext, contextType, ...context }) => ({
        ...context,
        actor: userContext,
        purchaseOrder: purchaseOrderContext,
        action: contextType,
        eventTargetObject: {
          ...purchaseOrderContext,
          name: purchaseOrderContext.name ?? 'Purchase order',
          typeName: 'Purchase order' as AuditEventTargetObjectTypeName,
        },
      })
    );

const timeActivityAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('TIME_ACTIVITY'),
    properties: auditContextPropertiesSchema,
    timeActivity: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, timeActivity, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based on
    // how the properties are used.
    ...context,
    actor: userContext,
    timeActivity: timeActivity,
    action: contextType,
    eventTargetObject: {
      ...timeActivity,
      name: timeActivity.name || 'Time record',
      typeName: 'Time record' as AuditEventTargetObjectTypeName,
    },
  }));

const customerAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('CUSTOMER'),
    properties: auditContextPropertiesSchema,
    customerContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, customerContext, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based on
    // how the properties are used.
    ...context,
    actor: userContext,
    customer: customerContext,
    action: contextType,
    eventTargetObject: {
      ...customerContext,
      typeName: 'Customer' as AuditEventTargetObjectTypeName,
    },
  }));

const contactAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('CONTACT'),
    properties: auditContextPropertiesSchema,
    contactContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    customerContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(
    ({
      userContext,
      contactContext,
      customerContext,
      contextType,
      ...context
    }) => ({
      ...context,
      actor: userContext,
      contact: contactContext,
      customer: customerContext,
      action: contextType,
      eventTargetObject: {
        ...contactContext,
        typeName: 'Contact' as AuditEventTargetObjectTypeName,
      },
    })
  );

const mergeEntityContextSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
});

/**
 * Builds the audit context schema for an entity merge event. The wire payload
 * carries entity-specific retained/removed keys (e.g. retainedCustomerContext),
 * which the preprocess step maps onto shared keys so one schema and transform
 * serve every merge entity.
 */
function createMergeAuditEventContextSchema<Type extends string>(
  typeLiteral: Type,
  retainedKey: string,
  removedKey: string,
  typeName: AuditEventTargetObjectTypeName
) {
  return z.preprocess(
    (value) => {
      if (value === null || typeof value !== 'object') {
        return value;
      }
      const raw = value as Record<string, unknown>;
      return {
        ...raw,
        retainedEntityContext: raw[retainedKey],
        removedEntityContext: raw[removedKey],
      };
    },
    baseAuditEventContextSchema
      .extend({
        type: z.literal(typeLiteral),
        properties: auditContextPropertiesSchema,
        retainedEntityContext: mergeEntityContextSchema,
        removedEntityContext: mergeEntityContextSchema,
      })
      .transform(
        ({
          userContext,
          retainedEntityContext,
          removedEntityContext,
          contextType,
          ...context
        }) => ({
          // Rename the properties from the API response to sensible names
          // based on how the properties are used.
          ...context,
          actor: userContext,
          retained: retainedEntityContext,
          removed: removedEntityContext,
          action: contextType,
          eventTargetObject: {
            id: retainedEntityContext.id,
            key: retainedEntityContext.key,
            name: retainedEntityContext.name,
            typeName,
          },
        })
      )
  );
}

const customerMergeAuditEventContextSchema = createMergeAuditEventContextSchema(
  'CUSTOMER_MERGE',
  'retainedCustomerContext',
  'removedCustomerContext',
  'Customer'
);

const contactMergeAuditEventContextSchema = createMergeAuditEventContextSchema(
  'CONTACT_MERGE',
  'retainedContactContext',
  'removedContactContext',
  'Contact'
);

const locationMergeAuditEventContextSchema = createMergeAuditEventContextSchema(
  'LOCATION_MERGE',
  'retainedLocationContext',
  'removedLocationContext',
  'Location'
);

const itemMergeAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('ITEM_MERGE'),
    properties: auditContextPropertiesSchema,
    itemContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, itemContext, contextType, ...context }) => ({
    ...context,
    actor: userContext,
    item: itemContext,
    action: contextType,
    eventTargetObject: {
      ...itemContext,
      typeName: 'Item' as AuditEventTargetObjectTypeName,
    },
  }));

const itemAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('ITEM'),
    properties: auditContextPropertiesSchema,
    itemContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, itemContext, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based on
    // how the properties are used.
    ...context,
    actor: userContext,
    item: itemContext,
    action: contextType,
    eventTargetObject: {
      ...itemContext,
      typeName: 'Item' as AuditEventTargetObjectTypeName,
    },
  }));

const bundleAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('BUNDLE'),
    properties: auditContextPropertiesSchema,
    bundleContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, bundleContext, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based on
    // how the properties are used.
    ...context,
    actor: userContext,
    bundle: bundleContext,
    action: contextType,
    eventTargetObject: {
      ...bundleContext,
      typeName: 'Bundle' as AuditEventTargetObjectTypeName,
    },
  }));

const itemPricingAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('ITEM_PRICING'),
    properties: auditContextPropertiesSchema,
    itemContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, itemContext, contextType, ...context }) => ({
    ...context,
    actor: userContext,
    item: itemContext,
    action: contextType,
    eventTargetObject: {
      ...itemContext,
      typeName: 'Item' as AuditEventTargetObjectTypeName,
    },
  }));

const itemFieldAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('ITEM_FIELD'),
    properties: auditContextPropertiesSchema,
    itemContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    itemFieldContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(
    ({
      userContext,
      itemContext,
      itemFieldContext,
      contextType,
      ...context
    }) => ({
      // Rename the properties from the API response to sensible names based on
      // how the properties are used.
      ...context,
      actor: userContext,
      field: itemFieldContext,
      item: itemContext,
      action: contextType,
      eventTargetObject: {
        ...itemContext,
        typeName: 'Item' as AuditEventTargetObjectTypeName,
      },
    })
  );

const organizationAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('ORGANIZATION'),
    properties: auditContextPropertiesSchema,
    organizationContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(
    ({ userContext, organizationContext, contextType, ...context }) => ({
      // Rename the properties from the API response to sensible names based on
      // how the properties are used.
      ...context,
      actor: userContext,
      organization: organizationContext,
      action: contextType,
      eventTargetObject: {
        ...organizationContext,
        typeName: 'Organization' as AuditEventTargetObjectTypeName,
      },
    })
  );

const organizationMemberAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.enum(['ORGANIZATION_MEMBER', 'PERSON_ORGANIZATION_MEMBERSHIP']),
    properties: auditContextPropertiesSchema,
    organizationContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    memberContext: z
      .object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
      })
      .optional(), // This is newly included as of March 2025 and did not exist in previous audit logs
  })
  .transform(
    ({
      userContext,
      organizationContext,
      memberContext,
      contextType,
      ...context
    }) => ({
      // Rename the properties from the API response to sensible names based
      // on how the properties are used.
      ...context,
      actor: userContext,
      organization: organizationContext,
      member: memberContext,
      action: contextType,
      eventTargetObject: {
        ...organizationContext,
        typeName: 'Organization' as AuditEventTargetObjectTypeName,
      },
    })
  );

const organizationAccountingAuditEventContextSchema =
  baseAuditEventContextSchema
    .extend({
      type: z.literal('ORGANIZATION_ACCOUNTING'),
      properties: auditContextPropertiesSchema,
      organizationContext: z.object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
      }),
    })
    .transform(
      ({ userContext, organizationContext, contextType, ...context }) => ({
        ...context,
        actor: userContext,
        organization: organizationContext,
        action: contextType,
        eventTargetObject: {
          ...organizationContext,
          typeName: 'Organization' as AuditEventTargetObjectTypeName,
        },
      })
    );

const organizationAttentionThresholdsAuditEventContextSchema =
  baseAuditEventContextSchema
    .extend({
      type: z.literal('ORGANIZATION_ATTENTION_THRESHOLDS'),
      properties: auditContextPropertiesSchema,
      organizationContext: z.object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
      }),
    })
    .transform(
      ({ userContext, organizationContext, contextType, ...context }) => ({
        ...context,
        actor: userContext,
        organization: organizationContext,
        action: contextType,
        eventTargetObject: {
          ...organizationContext,
          typeName: 'Organization' as AuditEventTargetObjectTypeName,
        },
      })
    );

const organizationWorkflowAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('ORGANIZATION_PROJECT_WORKFLOW'),
    properties: auditContextPropertiesSchema,
    workflowContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, workflowContext, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based
    // on how the properties are used.
    ...context,
    actor: userContext,
    workflow: workflowContext,
    action: contextType,
    eventTargetObject: {
      ...workflowContext,
      typeName: 'Organization' as AuditEventTargetObjectTypeName,
    },
  }));

const projectWorkflowCascadeAuditEventContextSchema =
  baseAuditEventContextSchema
    .extend({
      type: z.literal('PROJECT_WORKFLOW_CASCADE'),
      properties: auditContextPropertiesSchema,
      projectContext: z.object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
      }),
      oldWorkflowContext: z.object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
      }),
      newWorkflowContext: z.object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
      }),
    })
    .transform(
      ({
        userContext,
        projectContext,
        oldWorkflowContext,
        newWorkflowContext,
        contextType,
        ...context
      }) => ({
        // Rename the properties from the API response to sensible names based
        // on how the properties are used.
        ...context,
        actor: userContext,
        project: projectContext,
        oldWorkflow: oldWorkflowContext,
        newWorkflow: newWorkflowContext,
        action: contextType,
        eventTargetObject: {
          ...projectContext,
          typeName: 'Project' as AuditEventTargetObjectTypeName,
        },
      })
    );

const projectCostCascadeAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('PROJECT_COST_CASCADE'),
    properties: auditContextPropertiesSchema,
    projectContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    costContext: z.object({
      id: z.string(),
      key: z.string(),
      dateIncurredOn: z.string(),
      costAmount: auditCurrencyValueSchema.nullable(),
    }),
  })
  .transform(
    ({
      userContext,
      projectContext,
      costContext,
      contextType,
      ...context
    }) => ({
      // Rename the properties from the API response to sensible names based
      // on how the properties are used.
      ...context,
      actor: userContext,
      project: projectContext,
      cost: costContext,
      action: contextType,
      eventTargetObject: {
        ...projectContext,
        typeName: 'Project' as AuditEventTargetObjectTypeName,
      },
    })
  );

const projectChargeCascadeAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('PROJECT_CHARGE_CASCADE'),
    properties: auditContextPropertiesSchema,
    projectContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    // Single charge cascade (cancel) — present on single-charge events
    chargeContext: z
      .object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
      })
      .optional(),
    projectLineItemContext: z
      .object({
        id: z.string(),
        key: z.string(),
      })
      .optional(),
  })
  .transform(
    ({
      userContext,
      projectContext,
      chargeContext,
      projectLineItemContext,
      contextType,
      ...context
    }) => ({
      ...context,
      actor: userContext,
      project: projectContext,
      charge: chargeContext
        ? { ...chargeContext, label: chargeContext.name }
        : undefined,
      projectLineItem: projectLineItemContext,
      action: contextType,
      eventTargetObject: {
        ...projectContext,
        typeName: 'Project' as AuditEventTargetObjectTypeName,
      },
    })
  );

const projectTimeActivityCascadeAuditEventContextSchema =
  baseAuditEventContextSchema
    .extend({
      type: z.literal('PROJECT_TIME_ACTIVITY_CASCADE'),
      properties: auditContextPropertiesSchema,
      projectContext: z.object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
      }),
      timeActivityContext: z.object({
        id: z.string(),
        key: z.string(),
        hourlyRate: auditCurrencyValueSchema.nullable(),
        incurredOn: z.string(),
        durationMinutes: z.number().nullable(),
      }),
    })
    .transform(
      ({
        userContext,
        projectContext,
        timeActivityContext,
        contextType,
        ...context
      }) => ({
        // Rename the properties from the API response to sensible names based
        // on how the properties are used.
        ...context,
        actor: userContext,
        project: projectContext,
        timeActivity: timeActivityContext,
        action: contextType,
        eventTargetObject: {
          ...projectContext,
          typeName: 'Project' as AuditEventTargetObjectTypeName,
        },
      })
    );

const personAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('PERSON'),
    properties: auditContextPropertiesSchema,
    personContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, personContext, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based on
    // how the properties are used.
    ...context,
    actor: userContext,
    person: personContext,
    action: contextType,
    eventTargetObject: {
      ...personContext,
      typeName: 'User' as AuditEventTargetObjectTypeName,
    },
  }));

const notificationConfigurationAuditContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('PERSON_NOTIFICATION'),
    properties: auditContextPropertiesSchema,
    organization: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based on
    // how the properties are used.
    ...context,
    actor: userContext,
    action: contextType,
    eventTargetObject: {
      ...userContext,
      key: null,
      typeName: 'Notification settings' as AuditEventTargetObjectTypeName,
    },
  }));

const connectedApplicationAuditContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('PERSON_CONNECTED_APPLICATION'),
    properties: auditContextPropertiesSchema,
    organization: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    application: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, contextType, ...context }) => ({
    ...context,
    actor: userContext,
    action: contextType,
    eventTargetObject: {
      ...context.application,
      typeName: 'Connected application' as AuditEventTargetObjectTypeName,
    },
  }));

const hubspotAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('HUBSPOT'),
    properties: auditContextPropertiesSchema,
    description: z.string().optional().nullable(),
    organizationContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based on
    // how the properties are used.
    ...context,
    actor: userContext,
    action: contextType,
    eventTargetObject: {
      ...userContext,
      key: null,
      typeName: 'HubSpot configuration' as AuditEventTargetObjectTypeName,
    },
  }));

/**
 * HubSpot action summary events track CRM synchronization operations.
 * These are emitted when HubSpot data is processed (created, updated, etc).
 */
const hubspotActionSummaryAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('HUBSPOT_ACTION_SUMMARY'),
    properties: auditContextPropertiesSchema,
    organizationContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    entityType: z.string(),
    items: z.array(
      z.object({
        hubspotId: z.string(),
        hardfinId: z.string().nullable(),
      })
    ),
  })
  .transform(
    ({ userContext, contextType, organizationContext, ...context }) => ({
      ...context,
      actor: userContext,
      action: contextType,
      organization: organizationContext,
      eventTargetObject: {
        ...organizationContext,
        typeName: 'HubSpot sync' as AuditEventTargetObjectTypeName,
      },
    })
  );

const sfdcAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('SFDC'),
    properties: auditContextPropertiesSchema,
    description: z.string().optional().nullable(),
    organizationContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based on
    // how the properties are used.
    ...context,
    actor: userContext,
    action: contextType,
    eventTargetObject: {
      ...userContext,
      key: null,
      typeName: 'Salesforce configuration' as AuditEventTargetObjectTypeName,
    },
  }));

const organizationIntegrationAuditEventContextSchema =
  baseAuditEventContextSchema
    .extend({
      type: z.literal('ORGANIZATION_INTEGRATION'),
      properties: auditContextPropertiesSchema,
      description: z.string().optional().nullable(),
      organizationContext: z.object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
      }),
    })
    .transform(({ userContext, contextType, ...context }) => ({
      // Rename the properties from the API response to sensible names based on
      // how the properties are used.
      ...context,
      actor: userContext,
      action: contextType,
      eventTargetObject: {
        ...userContext,
        key: null,
        typeName: 'Integration configuration' as AuditEventTargetObjectTypeName,
      },
    }));

const projectDeleteAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('PROJECT_DELETE'),
    properties: auditContextPropertiesSchema,
    description: z.string().optional().nullable(),
    projectContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(({ userContext, contextType, ...context }) => ({
    ...context,
    actor: userContext,
    action: contextType,
    eventTargetObject: {
      ...userContext,
      key: null,
      typeName: 'Project deletion' as AuditEventTargetObjectTypeName,
    },
  }));

const noteAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('NOTE'),
    properties: auditContextPropertiesSchema,
    noteContext: z.object({
      id: z.string(),
      key: z.string(),
    }),
  })
  .transform(({ userContext, noteContext, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based on
    // how the properties are used.
    ...context,
    actor: userContext,
    note: noteContext,
    action: contextType,
    eventTargetObject: {
      ...noteContext,
      typeName: 'Note' as AuditEventTargetObjectTypeName,
      name: (
        context.properties.find(
          (property) => property.type === 'TEXT' && property.name === 'Content'
        ) as { newValue: string | null } | undefined
      )?.newValue,
    },
  }));

// Single-charge audit context emitted by event/audit/charge.go. Covers
// CHARGE_CREATE / CHARGE_UPDATE / CHARGE_DELETE state triggers. The context
// only carries the charge id + key — the project cascade carries richer
// hierarchy data and lives in projectChargeCascadeAuditEventContextSchema.
const chargeAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('CHARGE'),
    properties: auditContextPropertiesSchema,
    chargeContext: z.object({
      id: z.string(),
      key: z.string(),
    }),
  })
  .transform(({ userContext, chargeContext, contextType, ...context }) => ({
    ...context,
    actor: userContext,
    charge: chargeContext,
    action: contextType,
    eventTargetObject: {
      ...chargeContext,
      // The single-charge audit context does not include a label, so fall
      // back to the human-readable nano-id key for the event target name.
      // Drawer consumers display the full property delta directly so the
      // name only needs to identify which charge is being referenced.
      name: chargeContext.key,
      typeName: 'Charge' as AuditEventTargetObjectTypeName,
    },
  }));

// CHARGE_SPLIT audit context emitted when SplitCharge creates a new slice
// from an existing charge. Carries both the new charge and a back-reference
// to the source charge so the audit drawer can render "split from
// <original label>" without joining back to the charge table. The property
// delta is the same full create snapshot a CHARGE_CREATE would have carried.
const chargeSplitAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('CHARGE_SPLIT'),
    properties: auditContextPropertiesSchema,
    chargeContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    originalChargeContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(
    ({
      userContext,
      chargeContext,
      originalChargeContext,
      contextType,
      ...context
    }) => ({
      ...context,
      actor: userContext,
      charge: chargeContext,
      originalCharge: originalChargeContext,
      action: contextType,
      eventTargetObject: {
        ...chargeContext,
        // Split audits carry the new slice's label, so use it directly as
        // the target display name (more informative than the nano-id key).
        name: chargeContext.name,
        typeName: 'Charge' as AuditEventTargetObjectTypeName,
      },
    })
  );

// INVOICE_CREATE / INVOICE_UPDATE state triggers emitted when the sweep
// creates or appends to an invoice. The context carries the invoice id, key,
// and invoice number for display in the audit drawer.
const invoiceAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('INVOICE'),
    properties: auditContextPropertiesSchema,
    invoiceContext: z.object({
      id: z.string(),
      key: z.string(),
      invoiceNumber: z.string(),
    }),
    projectContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    customerContext: z
      .object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
      })
      .nullable(),
  })
  .transform(
    ({
      userContext,
      invoiceContext,
      projectContext,
      customerContext,
      contextType,
      ...context
    }) => ({
      ...context,
      actor: userContext,
      invoice: invoiceContext,
      project: projectContext,
      customer: customerContext,
      action: contextType,
      eventTargetObject: {
        ...invoiceContext,
        name: invoiceContext.invoiceNumber,
        typeName: 'Invoice' as AuditEventTargetObjectTypeName,
      },
    })
  );

// INVOICE_PAYMENT_CASCADE emitted when a payment is recorded against an
// invoice. Cascades to the invoice audit trail so the invoice history shows
// the payment application with amount, date, reference, and payment context.
const invoicePaymentCascadeAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('INVOICE_PAYMENT_CASCADE'),
    properties: auditContextPropertiesSchema,
    invoiceContext: z.object({
      id: z.string(),
      key: z.string(),
      invoiceNumber: z.string(),
    }),
    paymentContext: z.object({
      id: z.string(),
      key: z.string(),
    }),
    customerContext: z
      .object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
      })
      .nullable(),
  })
  .transform(
    ({
      userContext,
      invoiceContext,
      paymentContext,
      customerContext,
      contextType,
      ...context
    }) => ({
      ...context,
      actor: userContext,
      invoice: invoiceContext,
      payment: paymentContext,
      customer: customerContext,
      action: contextType,
      eventTargetObject: {
        ...invoiceContext,
        name: invoiceContext.invoiceNumber,
        typeName: 'Invoice' as AuditEventTargetObjectTypeName,
      },
    })
  );

// PROJECT_INVOICE_CASCADE emitted when a charge sweep creates or updates an
// invoice for a project. Cascades to the project audit trail with the invoice
// reference so the project history shows "invoice created".
const projectInvoiceCascadeAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('PROJECT_INVOICE_CASCADE'),
    properties: auditContextPropertiesSchema,
    projectContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    invoiceContext: z.object({
      id: z.string(),
      key: z.string(),
      invoiceNumber: z.string(),
    }),
  })
  .transform(
    ({
      userContext,
      projectContext,
      invoiceContext,
      contextType,
      ...context
    }) => ({
      ...context,
      actor: userContext,
      project: projectContext,
      invoice: invoiceContext,
      action: contextType,
      eventTargetObject: {
        ...projectContext,
        typeName: 'Project' as AuditEventTargetObjectTypeName,
      },
    })
  );

const ticketAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.enum([
      'TICKET_UPDATE',
      'TICKET_CREATE',
      'TICKET_FILE_CREATE',
      'TICKET_FILE_DELETE',
      'TICKET_NOTE',
      'TICKET_NOTE_UPDATE',
      'TICKET_NOTE_BIND',
      'TICKET_NOTE_UNBIND',
    ]),
    properties: auditContextPropertiesSchema,
    ticketContext: sparseEntitySchema,
  })
  .transform(({ userContext, ticketContext, contextType, ...context }) => ({
    // Rename the properties from the API response to sensible names based on
    // how the properties are used.
    ...context,
    actor: userContext,
    ticket: ticketContext,
    action: contextType,
    eventTargetObject: {
      ...ticketContext,
      typeName: 'Ticket' as AuditEventTargetObjectTypeName,
      name: ticketContext.name,
      key: ticketContext.key,
    },
  }));

const externalUserAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('ORGANIZATION_EXTERNAL_USER'),
    properties: auditContextPropertiesSchema,
    externalUserContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    organizationContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
  })
  .transform(
    ({
      userContext,
      externalUserContext,
      organizationContext,
      contextType,
      ...context
    }) => ({
      // Rename the properties from the API response to sensible names based on
      // how the properties are used.
      ...context,
      actor: userContext,
      externalUser: externalUserContext,
      organization: organizationContext,
      action: contextType,
      eventTargetObject: {
        ...externalUserContext,
        typeName: 'External notification' as AuditEventTargetObjectTypeName,
      },
    })
  );

const dataExportAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('DATA_EXPORT'),
    properties: auditContextPropertiesSchema,
    organizationContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    fileContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
    }),
    entityType: z.string(),
  })
  .transform(
    ({
      userContext,
      organizationContext,
      fileContext,
      contextType,
      ...context
    }) => ({
      ...context,
      actor: userContext,
      organization: organizationContext,
      file: fileContext,
      action: contextType,
      eventTargetObject: {
        ...organizationContext,
        typeName: 'Organization' as AuditEventTargetObjectTypeName,
      },
    })
  );

const externalLinkAuditEventContextSchema = baseAuditEventContextSchema
  .extend({
    type: z.literal('EXTERNAL_LINK'),
    properties: auditContextPropertiesSchema,
    entityContext: z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
      sku: z.string().nullable(),
    }),
    entityType: z.enum(['ITEM']),
    integrationSource: z.string(),
    externalId: z.string(),
  })
  .transform(({ userContext, entityContext, contextType, ...context }) => ({
    ...context,
    actor: userContext,
    entity: entityContext,
    action: contextType,
    eventTargetObject: {
      ...entityContext,
      typeName: 'Item' as AuditEventTargetObjectTypeName,
    },
  }));

/**
 * Project-to-project reference events track changes to a project's "previous project" field.
 * These are emitted when a project's previous project relationship is changed,
 * such as when setting up a renewal project that refers back to its predecessor.
 *
 * - CREATE: Previous project set for a project
 * - UPDATE: Previous project changed from one project to another
 * - DELETE: Previous project cleared
 */
const projectReferenceAuditEventContextSchema = z
  .object({
    type: z.literal('PROJECT_PROJECT_REFERENCE'),
    contextType: z.enum(['CREATE', 'UPDATE', 'DELETE']),
    reason: z.string(),
    aggregationKey: z.string().nullable(),
    newEntity: sparseEntitySchema.nullable(),
    oldEntity: sparseEntitySchema.nullable(),
    invokingEntity: sparseEntitySchema,
    userContext: auditEventContextActorSchema,
    source: z
      .object({
        id: z.string().nullable().optional(),
        name: z.string(),
        type: auditSourceTypeSchema,
      })
      .nullable()
      .optional(),
  })
  .transform(
    ({
      userContext,
      contextType,
      invokingEntity,
      newEntity,
      source,
      ...context
    }) => ({
      ...context,
      actor: userContext,
      action: contextType,
      invokingProject: invokingEntity,
      referencedProject: newEntity,
      properties: [] as AuditContextProperty[],
      source,
      eventTargetObject: {
        ...invokingEntity,
        typeName: 'Project' as AuditEventTargetObjectTypeName,
      },
    })
  );

const auditEventContextSchema = z.union([
  organizationWorkflowAuditEventContextSchema,
  projectWorkflowCascadeAuditEventContextSchema,
  projectCostCascadeAuditEventContextSchema,
  projectChargeCascadeAuditEventContextSchema,
  projectTimeActivityCascadeAuditEventContextSchema,
  assetAuditEventContextSchema,
  costAuditEventContextSchema,
  shipmentAuditEventContextSchema,
  fulfillmentOrderAuditEventContextSchema,
  fulfillmentOrderThirdPartyAuditEventContextSchema,
  returnOrderAuditEventContextSchema,
  returnOrderThirdPartyAuditEventContextSchema,
  purchaseOrderAuditEventContextSchema,
  purchaseOrderThirdPartyAuditEventContextSchema,
  timeActivityAuditEventContextSchema,
  contactAuditEventContextSchema,
  customerAuditEventContextSchema,
  customerMergeAuditEventContextSchema,
  contactMergeAuditEventContextSchema,
  locationAuditEventContextSchema,
  locationZoneAuditEventContextSchema,
  organizationAuditEventContextSchema,
  organizationMemberAuditEventContextSchema,
  organizationAccountingAuditEventContextSchema,
  organizationAttentionThresholdsAuditEventContextSchema,
  personAuditEventContextSchema,
  projectAuditEventContextSchema,
  itemAuditEventContextSchema,
  bundleAuditEventContextSchema,
  itemPricingAuditEventContextSchema,
  itemFieldAuditEventContextSchema,
  notificationConfigurationAuditContextSchema,
  connectedApplicationAuditContextSchema,
  hubspotAuditEventContextSchema,
  hubspotActionSummaryAuditEventContextSchema,
  sfdcAuditEventContextSchema,
  organizationIntegrationAuditEventContextSchema,
  projectDeleteAuditEventContextSchema,
  noteAuditEventContextSchema,
  chargeAuditEventContextSchema,
  chargeSplitAuditEventContextSchema,
  invoiceAuditEventContextSchema,
  invoicePaymentCascadeAuditEventContextSchema,
  projectInvoiceCascadeAuditEventContextSchema,
  ticketAuditEventContextSchema,
  locationMergeAuditEventContextSchema,
  itemMergeAuditEventContextSchema,
  externalUserAuditEventContextSchema,
  dataExportAuditEventContextSchema,
  externalLinkAuditEventContextSchema,
  projectReferenceAuditEventContextSchema,
]);

export type AuditEventContext = z.infer<typeof auditEventContextSchema>;

export const auditEventSchema = z.object({
  id: z.string(),
  key: z.string(),
  eventIdentifier: z.string(),
  context: auditEventContextSchema,
  triggeredAt: dateTimeSchema,
});

export type AuditEvent = z.infer<typeof auditEventSchema>;

/**
 * Tolerantly parse an array of audit events, filtering out invalid entries.
 * Uses safeParse per event so a single malformed event doesn't break the page.
 *
 * Uses a for-loop instead of reduce with spread to avoid O(n²) complexity
 * when parsing large datasets.
 */
const tolerantAuditEventsSchema = z
  // eslint-disable-next-line no-restricted-syntax -- Backend context is json.RawMessage (polymorphic). Parse each tolerantly and filter invalid.
  .array(z.unknown())
  .transform((events) => {
    const validEvents: AuditEvent[] = [];

    for (const event of events) {
      const result = auditEventSchema.safeParse(event);

      if (result.success) {
        validEvents.push(result.data);
      } else {
        if (import.meta.env.MODE === 'development') {
          console.error('Invalid audit event:', event, result.error);
        }

        Sentry.captureException(
          Error('Invalid audit event', { cause: event }),
          { extra: { event, error: result.error } }
        );
      }
    }

    return validEvents;
  });

/**
 * Schema for parsing entity-specific audit events API responses.
 * Used by endpoints like GetAuditForAsset that return a flat array.
 */
export const auditEventsResponseSchema = z
  .object({
    data: tolerantAuditEventsSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, events: data }));

/**
 * Schema for parsing the paginated full audit events API response.
 * The backend returns { results: [...], totalCount: number } in the data field.
 */
export const paginatedAuditEventsResponseSchema = z
  .object({
    data: z.object({
      results: tolerantAuditEventsSchema,
      totalCount: z.number().int(),
    }),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    events: data.results,
    totalCount: data.totalCount,
  }));
